'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import useAnimationReduced from '@haaretz/s-atoms/animationReduced';
import discardFields from '@haaretz/s-common-utils/discardFields';
import isHtmlVideoElement from '@haaretz/s-type-predicates/isHtmlVideoElement';
import useIntersectionObserver from '@haaretz/s-use-intersection-observer/common';
import React from 'react';
import s9 from 'style9';
const c = s9.create({
    base: {
        color: 'var(--altColor, transparent)'
    },
    video: {
        height: 'auto',
        maxWidth: '100%',
        verticalAlign: 'middle',
        width: '100%',
        objectFit: 'cover'
    },
    lazyVideo: {
        opacity: 0,
        transitionProperty: 'opacity',
        transitionDuration: '0.3s'
    },
    loadedVideo: {
        opacity: 1
    }
});
export const resetArgs = [
    'type',
    'kind',
    '__typename',
    'files'
];
export default function VideoElement({ src, inlineStyle, loading, width, height, styleExtend = [], onLoadingCompleteRef, setShowAltText, onError, alt, posterSrc, title, srcSet, ...attrs }) {
    const cleanedAttrs = discardFields(attrs, resetArgs);
    const videoRef = React.useRef(null);
    const [videoReady, setVideoReady] = React.useState(false);
    const animationReduced = useAnimationReduced();
    const isLazy = loading === 'lazy' || typeof loading === 'undefined';
    const finalSrcSet = React.useMemo(()=>{
        if (!srcSet || !animationReduced) return srcSet;
        return srcSet.replaceAll(/\.(gif|mp4)(\?[^\s,]*)?/gi, (match, ext, query)=>{
            const params = new URLSearchParams(query || '');
            params.set('frame', '1');
            return `.${ext}?${params.toString()}`;
        });
    }, [
        srcSet,
        animationReduced
    ]);
    const handleIntersection = React.useCallback((entries)=>{
        entries.forEach((entry)=>{
            if (entry.isIntersecting && isHtmlVideoElement(entry.target)) {
                const video = entry.target;
                if (video.dataset.src) {
                    video.src = video.dataset.src;
                    video.removeAttribute('data-src');
                }
                video.load();
                video.play().catch((error)=>{
                    console.error('Video auto-play was prevented by browser policy. User interaction may be required.', error);
                });
                setVideoReady(true);
            }
        });
    }, []);
    useIntersectionObserver({
        elements: isLazy && !animationReduced ? videoRef : undefined,
        cb: handleIntersection,
        rootMargin: '500px'
    });
    React.useEffect(()=>{
        if (!isLazy && !animationReduced) {
            setVideoReady(true);
        }
    }, [
        isLazy,
        animationReduced
    ]);
    const handleLoadedData = React.useCallback((e)=>{
        setVideoReady(true);
        if (onLoadingCompleteRef?.current) {
            onLoadingCompleteRef.current(e.currentTarget);
        }
        if (!isLazy) {
            const video = e.currentTarget;
            video.play()?.catch((error)=>{
                console.error('Video auto-play was prevented by browser policy. User interaction may be required.', error);
            });
            setVideoReady(true);
        }
    }, [
        onLoadingCompleteRef,
        isLazy
    ]);
    if (animationReduced) {
        return /* eslint-disable @next/next/no-img-element */ /*#__PURE__*/ _jsx("img", {
            "data-chromatic": "ignore",
            ...cleanedAttrs,
            alt: alt,
            title: title,
            width: width,
            height: height,
            srcSet: finalSrcSet,
            src: posterSrc,
            className: s9(c.base, ...styleExtend),
            loading: isLazy ? 'lazy' : loading,
            style: {
                ...inlineStyle
            }
        });
    }
    return /*#__PURE__*/ _jsx("video", {
        "data-testid": "gif-as-video",
        ...cleanedAttrs,
        ref: videoRef,
        width: width,
        height: height,
        poster: posterSrc,
        title: title,
        "aria-label": alt,
        autoPlay: true,
        muted: true,
        loop: true,
        controls: false,
        playsInline: true,
        className: s9(c.base, c.video, isLazy && !videoReady && c.lazyVideo, videoReady && c.loadedVideo, ...styleExtend),
        style: inlineStyle,
        onLoadedData: handleLoadedData,
        onError: (event)=>{
            setShowAltText(true);
            if (onError) onError(event);
        },
        ...isLazy ? {
            'data-src': src
        } : {
            src
        }
    });
}

//# sourceMappingURL=GifAsVideo.js.map